import React, { useState } from 'react';
import { urlMap, getAuthHeaders } from '../../../utils';

const COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: 'selected', label: 'Selected for Work' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'testing', label: 'Testing' },
  { key: 'done', label: 'Done' },
];

const SEVERITY_COLOR = {
  critical: '#b91c1c',
  high: '#d97706',
  medium: '#2563eb',
  low: '#6b7280',
};

const SEVERITY_LABEL = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const SCOPE_BADGE_COLOR = {
  structural: '#3b82f6',
  thematic: '#ec4899',
};

/**
 * ProjectIssueKanbanCard — kanban card for project_issues table (structural/thematic).
 *
 * Props:
 *  - issue: object (with _source = 'structural' | 'thematic')
 *  - projectId: string
 *  - isDragging: boolean
 *  - onDragStart: (e) => void
 *  - onDragEnd: () => void
 *  - onUpdated: (updated) => void
 *  - onDeleted: (issueId) => void
 *  - onDetailClick: (issue) => void
 *  - onMoveToChapter: (issue) => void  — opens the move-to-chapter modal
 */
export default function ProjectIssueKanbanCard({ issue, projectId, isDragging, onDragStart, onDragEnd, onUpdated, onDeleted, onDetailClick, onMoveToChapter }) {
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);

  const color = SEVERITY_COLOR[issue.severity] || SEVERITY_COLOR.medium;
  const scopeColor = SCOPE_BADGE_COLOR[issue._source] || SCOPE_BADGE_COLOR.structural;
  const scopeLabel = issue._source === 'thematic' ? 'Thematic' : 'Structural';

  const moveIssue = async (newStatus) => {
    setMoveMenuOpen(false);
    if (newStatus === issue.status) return;
    // Optimistic
    onUpdated({ ...issue, status: newStatus });
    try {
      const res = await fetch(urlMap.updateStructuralThematicIssueStatus(projectId, issue.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) throw new Error('Failed to update status');
      const updated = await res.json();
      onUpdated({ ...updated, _source: issue._source, status: updated.status });
    } catch {
      onUpdated(issue); // revert
    }
  };

  const deleteIssue = async () => {
    const ok = window.confirm('Delete this issue? This cannot be undone.');
    if (!ok) return;
    const res = await fetch(urlMap.deleteStructuralThematicIssue(projectId, issue.id), {
      method: 'DELETE',
      headers: getAuthHeaders(),
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Delete failed');
    onDeleted(issue.id);
  };

  const saveIssue = async ({ title, description, severity, scope }) => {
    const body = { title, description, severity };
    if (scope !== undefined && scope !== null) body.scope = scope;
    const res = await fetch(urlMap.updateStructuralThematicIssue(projectId, issue.id), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error('Save failed');
    const updated = await res.json();
    onUpdated({ ...updated, _source: updated.scope ?? issue._source });
  };

  return (
    <div
      className={`kanban-card${isDragging ? ' dragging' : ''}${issue.status === 'done' ? ' kanban-card--done' : ''}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={() => onDetailClick?.({ ...issue, _moveIssue: moveIssue, _saveIssue: saveIssue, _deleteIssue: deleteIssue, _openMoveToChapter: () => onMoveToChapter?.(issue) })}
    >
      <div className="kanban-card-top">
        <span className="kanban-card-severity" style={{ background: color }} />
        <span className="kanban-card-severity-label" style={{ color }}>
          {SEVERITY_LABEL[issue.severity] || issue.severity}
        </span>
        <span className="kanban-card-scope" style={{ background: `${scopeColor}18`, color: scopeColor }}>
          {scopeLabel}
        </span>
        <span style={{ flex: 1 }} />
        <span className="kanban-card-move" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="kanban-card-move-btn"
            title="Move to column"
            onClick={(e) => { e.stopPropagation(); setMoveMenuOpen((v) => !v); }}
          >
            ⋮
          </button>
          {moveMenuOpen && (
            <div className="kanban-card-move-menu">
              {COLUMNS.map((col) => (
                <button
                  key={col.key}
                  type="button"
                  className={`kanban-card-move-item${col.key === issue.status ? ' kanban-card-move-item--current' : ''}`}
                  onClick={(e) => { e.stopPropagation(); moveIssue(col.key); }}
                >
                  {col.key === issue.status ? `● ${col.label}` : col.label}
                </button>
              ))}
              {onMoveToChapter && (
                <>
                  <div className="kanban-card-move-divider" />
                  <button
                    type="button"
                    className="kanban-card-move-item kanban-card-move-item--chapter"
                    onClick={(e) => { e.stopPropagation(); setMoveMenuOpen(false); onMoveToChapter(issue); }}
                  >
                    ↗ Move to Chapter…
                  </button>
                </>
              )}
            </div>
          )}
        </span>
      </div>
      <div className="kanban-card-title">{issue.title || 'Untitled issue'}</div>
      {issue.issue_type && (
        <div className="kanban-card-footer">
          <span className="kanban-card-type">{String(issue.issue_type).replace(/_/g, ' ')}</span>
        </div>
      )}
    </div>
  );
}
